import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabaseClient } from '../../data/supabase-client';
import { ensureAppUser } from '../../data/writers/ensure-app-user';
import { getCaptureDraftStore } from '../../data/drafts/capture-draft';
import { getCaptureAudioStore } from '../../data/drafts/capture-audio';
import { getPhotoStagingStore } from '../../data/photos/photo-staging';
import { getSolConversationPointerStore } from '../../data/sol/sol-conversation-pointer';
import { readDeviceTimeZone } from '../../lib/timezone';
import { reportError } from '../../lib/monitoring';
import { readPublicAppUrl } from '../../lib/env';
import type { AppUserRow } from '../../data/database.types';

/**
 * Session state for the whole app.
 *
 * `status` is deliberately three-valued. "Not signed in" and "we do not know
 * yet" are different facts, and collapsing them is what produces a flash of a
 * protected screen — or a flash of the sign-in screen at every reload. Nothing
 * protected renders while the status is 'checking' (INV-T23).
 */
export type SessionStatus = 'checking' | 'signed-out' | 'signed-in';

export interface SessionState {
  status: SessionStatus;
  session: Session | null;
  profile: AppUserRow | null;
  /** True when no Supabase configuration is present at all. */
  unconfigured: boolean;
  // Declared as properties rather than methods on purpose: method shorthand
  // makes these unbound-method hazards when destructured out of the context,
  // which is exactly how every caller uses them.
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ error: string | null }>;
}

const SessionContext = createContext<SessionState | null>(null);

/**
 * The context itself, exported so tests can supply a known session state
 * without standing up a Supabase client. Production code uses `useSession`.
 */
export const SessionContextForTests = SessionContext;

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

/**
 * The session, or `null` when there is no provider above this component.
 *
 * For code whose ONLY use of the session is to answer "which authenticated
 * user is this?" and whose correct behaviour when the answer is unknown is to
 * do nothing. The durable Capture draft is exactly that: no identity means no
 * read and no write, which is the fail-closed rule S-04-01 requires anyway.
 *
 * `useSession` still throws, and should: a screen that RENDERS a signed-in
 * user's data must not silently render without one. This is the narrower
 * question, and asking it must not be a way around the wider guarantee.
 */
export function useOptionalSession(): SessionState | null {
  return useContext(SessionContext);
}

/**
 * THE authenticated owner id, or `null` — one rule, in one place.
 *
 * Every consumer of device-local, user-scoped material asks this same question
 * and must get the same answer, so it is a function rather than a line
 * repeated per screen. **`null` unless the status is `signed-in`**: `checking`
 * is not an identity, and treating it as one is how a draft would be read
 * before the session that owns it is resolved.
 *
 * The auth session's user id is authoritative; the profile row's `user_id` is
 * the same value (it IS `auth.uid()`, migration 0001) and is the fallback for
 * the case where the profile has loaded and the raw session object has not.
 */
export function sessionOwnerUserId(state: SessionState | null): string | null {
  if (!state || state.status !== 'signed-in') return null;
  return state.session?.user.id ?? state.profile?.user_id ?? null;
}

/**
 * Sign-in failures are shown to Brandon, so the wording matters (PT §20).
 *
 * Supabase deliberately returns the same "Invalid login credentials" for a
 * wrong password and an unknown email, so that the endpoint cannot be used to
 * discover which addresses have accounts. We keep that property — telling the
 * user which half was wrong would leak exactly what the generic message
 * protects — while still being truthful that the sign-in did not work.
 */
export function describeSignInError(message: string): string {
  const normalised = message.toLowerCase();
  if (normalised.includes('invalid login credentials')) {
    return 'That email and password do not match. Check them and try again.';
  }
  if (normalised.includes('email not confirmed')) {
    return 'This account is not confirmed yet. Ask Jerry to finish setting it up.';
  }
  if (normalised.includes('failed to fetch') || normalised.includes('network')) {
    return 'Could not reach Crewlink. Check your connection and try again.';
  }
  return 'Could not sign in. Please try again.';
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<SessionStatus>(client ? 'checking' : 'signed-out');
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AppUserRow | null>(null);

  // Resolve the stored session before anything protected can render, then keep
  // it in sync. Both halves are needed: the first answers "is he signed in?"
  // at startup, the second catches sign-out, token refresh and expiry.
  useEffect(() => {
    if (!client) return;

    let cancelled = false;

    void client.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        setSession(data.session);
        setStatus(data.session ? 'signed-in' : 'signed-out');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Treat an unreadable session as signed out. Failing closed is correct:
        // the worst case is one extra sign-in, versus rendering protected UI.
        reportError(error, 'auth.getSession');
        setSession(null);
        setStatus('signed-out');
      });

    const { data: subscription } = client.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setStatus(nextSession ? 'signed-in' : 'signed-out');
      if (!nextSession) setProfile(null);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [client]);

  // Seed the profile once we have a session. The database ignores the supplied
  // timezone if a profile already exists, so this cannot move a stored value.
  useEffect(() => {
    if (!client || !session || profile) return;

    let cancelled = false;
    void ensureAppUser(client, readDeviceTimeZone())
      .then((row) => {
        if (!cancelled) setProfile(row);
      })
      .catch((error: unknown) => {
        // A missing profile must not lock Brandon out of a screen he is
        // entitled to see. Report it and carry on without it.
        reportError(error, 'auth.ensureAppUser');
      });

    return () => {
      cancelled = true;
    };
  }, [client, session, profile]);

  const signIn = useCallback<SessionState['signIn']>(
    async (email, password) => {
      if (!client) return { error: 'Crewlink is not configured yet.' };
      const { error } = await client.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      return { error: error ? describeSignInError(error.message) : null };
    },
    [client],
  );

  const signOut = useCallback<SessionState['signOut']>(async () => {
    /**
     * THE SIGN-OUT PURGE — M4 / S-04-01.
     *
     * An EXPLICIT sign-out is one of the three terminal outcomes that removes
     * local recovery material (D-019). Handing the phone to someone else is
     * the whole reason the button exists, and an unfinished Capture left on
     * disk would be readable by the next person to sign in if the owner check
     * ever failed. It is removed here rather than relied on being hidden.
     *
     * DELIBERATELY NOT IN `onAuthStateChange`. That fires for token expiry and
     * refresh failure too, and S-04-01's rule is explicit: an expired session
     * must NOT destroy the draft — Brandon signs in again and it is still
     * there. Only the deliberate act purges, which is why the purge lives in
     * the deliberate act.
     *
     * It runs BEFORE the client check and is not conditional on the network
     * call succeeding: the user asked to be signed out, so the local material
     * goes regardless of what the server says.
     */
    const ownerUserId = session?.user.id ?? profile?.user_id ?? null;
    if (ownerUserId) {
      try {
        await getCaptureDraftStore().clear(ownerUserId);
      } catch (error) {
        reportError(error, 'auth.signOut.clearCaptureDraft');
      }
      // S-04-02, on Jerry's explicit decision (D-021): the temporary recording
      // goes with the draft. Handing the phone to someone else is what the
      // button is FOR, and a recording of a job site is exactly the kind of
      // thing that must not outlive the session that made it. Each store is
      // cleared independently so a failure in one cannot skip the other.
      try {
        await getCaptureAudioStore().clear(ownerUserId);
      } catch (error) {
        reportError(error, 'auth.signOut.clearCaptureAudio');
      }
      // S05-03, D-027: staged photos go the same way, for the same reason —
      // a sanitized construction photo still SHOWS the site, and it must not
      // outlive the deliberate act of signing out. Session expiry, as with the
      // other two stores, purges nothing: this call lives only here.
      try {
        await getPhotoStagingStore().purge(ownerUserId);
      } catch (error) {
        reportError(error, 'auth.signOut.clearStagedPhotos');
      }
      // M11 / I-065: the pointer to the Sol conversation being continued goes
      // too. **It is an identifier, not content** — the conversation itself
      // stays on the server, owned by whoever owns it, and RLS is what keeps it
      // theirs. What this prevents is the next person to sign in on this phone
      // finding the app pointed at a conversation that was never theirs, which
      // the server would refuse anyway. Belt, and braces.
      try {
        await getSolConversationPointerStore().forget(ownerUserId);
      } catch (error) {
        reportError(error, 'auth.signOut.clearSolConversation');
      }
    }

    if (!client) return;
    const { error } = await client.auth.signOut();
    if (error) reportError(error, 'auth.signOut');
    // Clear locally regardless: if the server call failed, the user still asked
    // to be signed out and must not be left looking at protected screens.
    setSession(null);
    setProfile(null);
    setStatus('signed-out');
  }, [client, session, profile]);

  const requestPasswordReset = useCallback<SessionState['requestPasswordReset']>(
    async (email) => {
      if (!client) return { error: 'Crewlink is not configured yet.' };

      // **THE DESTINATION IS CONFIGURED, NEVER INFERRED (H-3).** The reset
      // email opens the hosted WEB page — no native deep link in v1 (D-005) —
      // and this used to build that address from `window.location.origin`. On
      // Android the app runs in a WebView whose origin is `https://localhost`,
      // so the emailed link pointed at `https://localhost/reset-password`: a
      // link that can never open on any device.
      const appUrl = readPublicAppUrl();
      if (appUrl === null) {
        // **REFUSE RATHER THAN GUESS.** A fallback to the running origin is
        // what produced the dead link, and a hardcoded domain would ship one
        // environment's address to every other. No request is made, because a
        // request made now would send a real email nobody can use.
        return {
          error: 'This build cannot send reset emails yet. Ask whoever set up Crewlink for you.',
        };
      }

      const { error } = await client.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${appUrl}/reset-password`,
      });

      // **AN UNSENT EMAIL IS NOT A SENT EMAIL (H-3).** This used to return
      // success unconditionally, so a network outage, a misconfigured mail
      // sender or a rate limit all produced "a reset link is on its way" while
      // nothing had been sent — leaving Brandon waiting on an email that was
      // never coming, on the one screen he reaches when he is already locked
      // out.
      //
      // **This does NOT weaken enumeration opacity.** Supabase answers a
      // request for an unknown address the same way it answers a known one, so
      // an error here never means "no such account" — it means the request
      // itself did not get through. The identical-success guarantee is
      // untouched; only genuine failure is now told truthfully.
      if (error) {
        reportError(error, 'auth.requestPasswordReset');
        // **NAME THE OUTCOME, NEVER A CAUSE CREWLINK CANNOT SEE.** This one
        // branch covers being offline, Supabase Auth failing, the mail sender
        // failing, a rate limit and a misconfiguration — and the API gives no
        // reliable way to tell them apart. "Check your connection" therefore
        // blamed Brandon's phone for something that is usually not his phone,
        // and sent him to fix the wrong thing. What is certainly true is that
        // the email did not go and trying again later may work.
        //
        // The provider's own words are deliberately not shown either: they
        // leak sender and infrastructure detail and mean nothing to him.
        return { error: 'Crewlink could not send the reset email. Try again in a moment.' };
      }

      // Deliberately does NOT reveal whether the address has an account.
      return { error: null };
    },
    [client],
  );

  const value = useMemo<SessionState>(
    () => ({
      status,
      session,
      profile,
      unconfigured: !client,
      signIn,
      signOut,
      requestPasswordReset,
    }),
    [status, session, profile, client, signIn, signOut, requestPasswordReset],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
